/**
 * 有效 case 漏斗（mock v3 第二段）：提交 → 出 metrics → 真值加载成功 → 进交集。
 *
 * 存在的意义是把「指标算在多少个 case 上」摊开——总体召回率是 Σtp/Σgt，分母只含真正
 * 参与计算的 case。漏斗上的每一级落差都是一类被排除的原因，藏起来就会让人把
 * 「1024 个场景的召回率」和实际的 1012 个混为一谈。
 *
 * ``compared`` 仅在对比视图有值；单 job 视图为 null，此时不渲染那一级。
 */
import { Tooltip } from 'antd';

import type { PerceptionFunnel } from '@/apis/simulation';

/** 相对上一级的留存率；上一级为 0 时不显示（避免 0/0）。 */
const retention = (curr: number, prev: number | undefined): string | null => {
  if (prev === undefined || prev <= 0) return null;
  return `${((curr / prev) * 100).toFixed(1)}%`;
};

interface Step {
  label: string;
  value: number;
  hint: string;
}

interface FunnelProps {
  funnel: PerceptionFunnel;
  /** 业务展示名（如「OCC 占据」）；传了则第二、三级文案带上业务，按检查项口径解释 */
  taskName?: string;
}

export default function Funnel({ funnel, taskName }: FunnelProps) {
  const steps: Step[] = [
    { label: '提交场景', value: funnel.submitted, hint: 'job 中放入的场景数（按 task 去重）' },
    {
      label: '回传指标',
      value: funnel.withMetrics,
      hint: taskName
        ? '回传了该检查项指标块的场景；未声明该业务真值的场景不运行该检查项，也计入差额'
        : '算法侧回传了 perception_metrics 的场景；差额多为仿真未跑完 / 上报失败',
    },
    {
      label: taskName ? `${taskName}真值已加载` : '真值加载成功',
      value: funnel.gtLoaded,
      hint: taskName
        ? '该业务 gt.status == loaded；差额为真值读不了或下载失败'
        : 'gt_coverage.loaded == true；差额为真值缺失或下载失败',
    },
  ];
  if (funnel.compared !== null && funnel.compared !== undefined) {
    steps.push({
      label: '进入对比',
      value: funnel.compared,
      hint: '两个 job 都有指标的场景交集——单侧缺失的 case 无从比较，不拿单侧数据充数',
    });
  }

  return (
    <div className="pe-funnel">
      {steps.map((s, i) => {
        const rate = retention(s.value, i === 0 ? undefined : steps[i - 1].value);
        return (
          <div className="pe-funnel-step" key={s.label}>
            <div className="pe-funnel-step-label">
              <Tooltip title={s.hint}>{s.label}</Tooltip>
            </div>
            <div className="pe-funnel-step-value">{s.value.toLocaleString('zh-CN')}</div>
            <div className="pe-funnel-step-sub">
              {rate ? `留存 ${rate}` : ' '}
            </div>
          </div>
        );
      })}
    </div>
  );
}
